import React, { memo, useMemo } from "react"
import { css } from "@emotion/react"
import { Link, useHistory } from "react-router-dom"
import {
	Box,
	PartPriceEcomm,
	TrackingEvent,
	PartClearancePriceEcomm,
	ButtonEcomm,
	CardProps,
	Paragraph,
	calculateEcommDiscount,
	PartImage,
	EventTracker,
	Icon,
} from "@ncs/web-legos"
import {
	isNonParentOnlinePart,
	NonParentCustomerPart,
	NonParentOnlinePart,
	OnlinePartEcomm,
	useOnlinePartDetails,
} from "@ncs/ncs-api"
import { useShopContext } from "~/contexts"
import ComingSoon from "/parts/coming-soon.svg"
import { useAnalytics } from "~/hooks/useAnalytics"

type WidthModeType = "fixed" | "fluid"
interface PartCardProps extends CardProps {
	part: OnlinePartEcomm
	onAddToCart: (part: any) => void
	widthMode?: WidthModeType
}

function mapNonParentOnlinePartToNonParentCustomerPart(
	part: NonParentOnlinePart
): NonParentCustomerPart {
	return {
		id: String(part.id),
		description: part.description,
		flatShippingRate: null, // or map if available
		imageUrl: part.imageUrl ?? null,
		childImage: null, // or map if available
		isActive: Boolean(part.isActive),
		isShippedFlatRate: false, // or map if available
		netPrice: Number(part.netPrice),
		childNetPrice: null, // or map if available
		onlinePartNumber: part.onlinePartNumber ?? null,
		part: {
			id: part.partId ? String(part.partId) : String(part.part.id),
			description: part.description ?? String(part.part.description),
			partNumber: part.partNumber ?? String(part.part.partNumber),
			unitOfMeasure: part.unitOfMeasure ?? String(part.part.unitOfMeasure),
		}, // or map if available
		price: part.price,
		shippingLeadDays: 0, // or map if available
		title: part.title ?? null,
		isParent: Boolean(part.isParent),
		isChild: false, // or map if available
		variantCount: 0, // or map if available
		brandName: part.brandName ?? null,
		featuredItem: false, // or map if available
		clearanceItem: Boolean(part.clearanceItem),
		clearanceItemDiscount: null, // or map if available
		variants: [],
	}
}

export const PartCard: React.FC<PartCardProps> = memo(
	({ part, onAddToCart, widthMode, ...rest }) => {
		const history = useHistory()
		const [, shopDispatch] = useShopContext()
		const [partDetails] = useOnlinePartDetails(part.isParent ? part.id : undefined)
		const { trackEvent } = useAnalytics()

		const handleAddToCart = (partToAdd: NonParentOnlinePart) => {
			const customerPart = mapNonParentOnlinePartToNonParentCustomerPart(partToAdd)
			shopDispatch({
				type: "add part to cart",
				payload: { part: customerPart },
			})

			// Fire the passed in handler to open to the added to cart modal.
			onAddToCart(partToAdd)
			trackEvent(TrackingEvent.FLOATING_CART, {
				id: partToAdd.id,
				quantity: 1,
				item_value: partToAdd.price,
				name: partToAdd.part?.description,
				part_title_name: partToAdd.title,
				part_number: partToAdd.onlinePartNumber,
			})
		}

		const goToProduct = (id: string) => {
			history.push(`/shop/parts/${id}`)
			trackEvent(TrackingEvent.GO_TO_PRODUCT, { id, name: part.description })
		}

		const displayNetPrice = (cardPart: OnlinePartEcomm): number | null => {
			return cardPart.isParent ? cardPart.childNetPrice : cardPart.netPrice
		}

		const partCardImg = (cardPart: OnlinePartEcomm): string => {
			let img = ComingSoon
			if (cardPart.isParent) {
				img = cardPart.childImage ?? ComingSoon
			} else {
				img = cardPart.imageUrl ?? ComingSoon
			}
			return img
		}

		const isClearanceItem = useMemo(() => {
			if (part.isParent && partDetails && partDetails.children) {
				return partDetails.children.some((p) => p.clearanceItem)
			}
			return part.clearanceItem
		}, [part, partDetails])

		const [clearanceDiscount, clearancePrice] = useMemo(() => {
			if (part.isParent && partDetails && partDetails.children) {
				let discount = 0
				let price = 0

				for (const p of partDetails.children) {
					if (
						(calculateEcommDiscount(p.clearanceItemDiscount, p.price) ?? 0) > discount
					) {
						discount = calculateEcommDiscount(p.clearanceItemDiscount, p.price) ?? 0
						price = p.price
					}
				}

				return [discount, price]
			}
			return [calculateEcommDiscount(part.clearanceItemDiscount, part.price), part.price]
		}, [part, partDetails])

		return (
			<div css={() => partCard(widthMode ?? "fixed")}>
				<>
					{isClearanceItem && (
						<Box display="flex" flexDirection="row" css={clearanceTag}>
							<Icon icon="tag" />
							<span>
								{part?.isParent && "Up To"} {clearanceDiscount}% Off
							</span>
						</Box>
					)}
					<EventTracker
						event={TrackingEvent.CARD}
						data={{ id: part.id, name: part.description }}
						trackEvent={trackEvent}
					>
						<Link to={`/shop/parts/${part.id}`}>
							<PartImage
								src={partCardImg(part)}
								css={partImageContainerStyle}
								imageCss={stylePartImage}
								alt={part.title}
								className="part-card-image"
							/>
						</Link>
					</EventTracker>
					<Box display="flex" flexDirection="column" gap="8px">
						<EventTracker
							event={TrackingEvent.CARD}
							data={{ id: part.id, name: part.description }}
							trackEvent={trackEvent}
						>
							<Paragraph
								mb={0.5}
								color="secondary"
								fontSize={0.8}
								customColor={"#003264"}
								bold
								textTransform="uppercase"
							>
								{part?.brandName ? part.brandName : "n/a"}
							</Paragraph>
							<Link to={`/shop/parts/${part.id}`} css={headingLinkStyle}>
								<Paragraph css={productTitle} className="product-title">
									{part.title}
								</Paragraph>
							</Link>
						</EventTracker>
						{!isClearanceItem && (
							<>
								<Paragraph color="secondary" fontSize={0.8}>
									{isNonParentOnlinePart(part) ?
										`#${part.onlinePartNumber}`
									:	`${part?.variantCount} Options Available`}
								</Paragraph>
							</>
						)}
					</Box>
					<div>
						{isClearanceItem ?
							<PartClearancePriceEcomm
								netPrice={clearancePrice}
								price={part.clearanceItemDiscount}
								isParent={part?.isParent}
								clearanceDiscount={clearanceDiscount}
								showClearanceLegend
							/>
						:	<PartPriceEcomm
								netPrice={displayNetPrice(part) ?? 0}
								price={part?.price}
								isParent={part?.isParent}
							/>
						}
					</div>
				</>
				{isNonParentOnlinePart(part) ?
					<ButtonEcomm
						variant="primary-cta"
						onClick={() => handleAddToCart(part)}
						fillContainer
						containerProps={{
							mt: 2,
						}}
					>
						Add To Cart
					</ButtonEcomm>
				:	<ButtonEcomm
						variant="secondary-cta"
						onClick={() => goToProduct(part.id)}
						fillContainer
						containerProps={{
							mt: 2,
						}}
					>
						See Options
					</ButtonEcomm>
				}
			</div>
		)
	}
)

const partCard = (widthMode: WidthModeType = "fixed") => css`
	display: grid;
	grid-template-rows: auto 1fr auto auto;
	transition: box-shadow 0.3s ease-in-out;
	width: ${widthMode === "fixed" ? "186px" : "100%"};

	position: relative;
	z-index: 0;
	gap: 24px;

	a:hover,
	a:focus {
		text-decoration: none;
	}

	&:hover {
		cursor: pointer;
	}

	&:hover button.css-dvbig9-baseButtonStyle-buttonWidthStyle-variantStyle {
		background-color: #00254a;
	}

	&:hover button.css-upcgpj-baseButtonStyle-buttonWidthStyle-variantStyle {
		border: 2px solid #00254a;
		color: #00254a;
	}

	&:hover div.part-card-image {
		box-shadow: 0 0 0.375rem 0 #0b75e1;
	}

	&:hover p.product-title {
		color: #0b75e1;
	}
`

const clearanceTag = css`
	min-width: 102px;
	background-color: #dc2626;
	display: flex;
	align-items: center;
	padding: 0.3rem 12px;
	position: absolute;
	z-index: 1;
	border-radius: 4px 0px;

	svg {
		margin-top: 5px;
		margin-right: 8px;
	}

	> p,
	> span {
		color: #ffffff;
		font-size: 0.875rem;
		font-weight: 700;
	}
`

const headingLinkStyle = css`
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`

const partImageContainerStyle = css`
	width: 100%;
	padding: 13px;
	border-radius: 0.25rem;
	box-shadow: 0 0 0.375rem 0 #d1d5db;
	background-color: #fff;
	display: flex;
	align-items: center;
	aspect-ratio: 1/1;
`

const stylePartImage = css`
	min-height: 160px;
	width: 100%;
	object-fit: contain;
	object-position: center;
`

const productTitle = css`
	font-size: 1.25rem;
	font-weight: 500;
	line-height: normal;
	overflow-wrap: anywhere;
	overflow: hidden;
	text-overflow: ellipsis;
	display: block;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	line-height: 1.1;
	max-height: 4.5em;
	overflow: hidden;
`
